import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "components/ui/form";
import { Input } from "components/ui/input";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useFilterActions } from "../../../ducks/filter/hooks/action";
import { useFilterState } from "../../../ducks/filter/hooks/filterState";
import { formatCurrency } from "@/utils/formatToMoney";
import { Checkbox } from "components/ui/checkbox";

const AdvancedFiltersSchema = z.object({
  cardNumber: z.string().optional(),
  nsuNumber: z.string().optional(),
  nsuNetwork: z.string().optional(),
  operationNumber: z.string().optional(),
  couponNumber: z.string().optional(),
  salesControlNumber: z.string().optional(),
  initialValue: z.string().optional(),
  finalValue: z.string().optional(),
  isOnly1InSequence: z.boolean().optional().default(true),
});

export function ModalAdvancedFilters() {
  const { handleModal } = useDialogModal();
  const { updateFilter } = useFilterActions();
  const filterState = useFilterState();

  const form = useForm<z.infer<typeof AdvancedFiltersSchema>>({
    resolver: zodResolver(AdvancedFiltersSchema),
    defaultValues: {
      cardNumber: filterState?.filters?.advancedFilter?.cardNumber,
      nsuNumber: filterState?.filters?.advancedFilter?.nsuNumber,
      nsuNetwork: filterState?.filters?.advancedFilter?.nsuNetwork,
      operationNumber: filterState?.filters?.advancedFilter?.operationNumber,
      couponNumber: filterState?.filters?.advancedFilter?.couponNumber,
      salesControlNumber:
        filterState?.filters?.advancedFilter?.salesControlNumber,
      finalValue: filterState?.filters?.advancedFilter?.finalValue,
      initialValue: filterState?.filters?.advancedFilter?.initialValue,
      isOnly1InSequence:
        filterState?.filters?.advancedFilter?.isOnly1InSequence !== undefined
          ? filterState.filters.advancedFilter.isOnly1InSequence
          : true,
    },
  });

  async function onSubmit(data: z.infer<typeof AdvancedFiltersSchema>) {
    updateFilter({
      key: "advancedFilter",
      value: {
        ...filterState?.filters?.advancedFilter,
        ...data,
      },
    });
    handleModal({ isOpen: false });
  }

  function handleClearAdvancedFilters() {
    form.reset({
      cardNumber: "",
      nsuNumber: "",
      nsuNetwork: "",
      operationNumber: "",
      couponNumber: "",
      salesControlNumber: "",
      initialValue: "",
      finalValue: "",
      isOnly1InSequence: true,
    });
  }

  return (
    <div className="grid gap-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Filtros Avançados
            </h3>

            <div className="flex gap-4">
              <div className="flex-1 flex flex-col gap-1">
                <FormField
                  control={form.control}
                  name="cardNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Número do cartão</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          hasError={!!form.formState.errors.cardNumber}
                          placeholder="Número do cartão"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <FormField
                  control={form.control}
                  name="nsuNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>NSU de autorização</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          hasError={!!form.formState.errors.nsuNumber}
                          placeholder="NSU de autorização"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-1 flex flex-col gap-1">
                <FormField
                  control={form.control}
                  name="nsuNetwork"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>NSU da rede</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          hasError={!!form.formState.errors.nsuNetwork}
                          placeholder="NSU da rede"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <FormField
                  control={form.control}
                  name="operationNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Número da operação</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          hasError={!!form.formState.errors.operationNumber}
                          placeholder="Número da operação"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-1 flex flex-col gap-1">
                <FormField
                  control={form.control}
                  name="couponNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Número do cupom</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          hasError={!!form.formState.errors.couponNumber}
                          placeholder="Número do cupom"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <FormField
                  control={form.control}
                  name="salesControlNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Número de controle de vendas</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          hasError={!!form.formState.errors.salesControlNumber}
                          placeholder="Número de controle de vendas"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="w-full">
              <FormField
                control={form.control}
                name="initialValue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-gray-700">
                      Valor
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="R$ 0,00"
                        {...field}
                        onChange={(e) =>
                          field.onChange(formatCurrency(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage className="text-red-500 text-xs mt-1" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isOnly1InSequence"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 mt-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="isOnly1InSequence"
                      />
                    </FormControl>
                    <FormLabel
                      htmlFor="isOnly1InSequence"
                      className="mb-0 cursor-pointer"
                    >
                      Buscar apenas itens de sequência com número 1
                    </FormLabel>
                  </FormItem>
                )}
              />
            </div>

            <div className="w-full flex gap-4 justify-end pt-4 border-t">
              <Button
                size="sm"
                variant="outline"
                type="button"
                onClick={handleClearAdvancedFilters}
              >
                Limpar
              </Button>
              <Button
                size="sm"
                variant="ghost_none"
                type="button"
                onClick={() => handleModal({ isOpen: false })}
              >
                Cancelar
              </Button>
              <Button type="submit" variant="default" size="sm">
                Aplicar Filtros Avançados
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}